import { useEffect, useState, useSyncExternalStore } from 'react';

import type { RichBlock, RichDoc } from '../rich-doc';
import { attachmentHref } from '../store/files';
import { ANY_CHANGE_KEY, getVersion, requireCache, subscribeKeys } from '../store/runtime';
import { isPersonalAttachment } from '../storage-folders/system';
import type { AttachmentId, EpochMs, MockCache } from '../types';
import { docByShareToken } from './tree';

export interface PublicDocFile {
  name: string;
  url: string;
  mime: string;
  sizeBytes: number;
}

export interface PublicDocCover {
  name: string;
  url: string;
}

export interface PublicDoc {
  title: string;
  cover: PublicDocCover | null;
  bodyJson: RichDoc;
  ownerName: string;
  updatedAt: EpochMs;
  files: Record<string, PublicDocFile>;
}

export interface PublicDocBackend {
  fetchByToken(token: string): Promise<PublicDoc | null>;
}

let publicDocBackend: PublicDocBackend | null = null;

export function setPublicDocBackend(backend: PublicDocBackend | null): void {
  publicDocBackend = backend;
}

function fileBlocks(blocks: RichDoc): RichBlock[] {
  return blocks.flatMap((block) => [...(block.type === 'file' ? [block] : []), ...fileBlocks(block.children)]);
}

function publicFiles(cache: MockCache, body: RichDoc): Record<string, PublicDocFile> {
  const files: Record<string, PublicDocFile> = {};
  for (const block of fileBlocks(body)) {
    const id = String(block.props['attachmentId'] ?? '');
    const attachment = cache.attachments.get(id as AttachmentId);
    if (attachment !== undefined && !isPersonalAttachment(attachment)) {
      const { name, mime, sizeBytes } = attachment;
      files[id] = { name, url: attachmentHref(attachment) ?? attachment.url, mime, sizeBytes };
    }
  }
  return files;
}

function publicCover(cache: MockCache, attachmentId: AttachmentId | null): PublicDocCover | null {
  const attachment = attachmentId === null ? undefined : cache.attachments.get(attachmentId);
  return attachment === undefined || isPersonalAttachment(attachment)
    ? null
    : { name: attachment.name, url: attachmentHref(attachment) ?? attachment.url };
}

function publicDocFromCache(cache: MockCache, token: string): PublicDoc | null {
  const doc = docByShareToken(cache, token);
  if (doc === undefined) {
    return null;
  }
  return {
    title: doc.title,
    cover: publicCover(cache, doc.coverAttachmentId),
    bodyJson: doc.bodyJson,
    ownerName: cache.users.get(doc.ownerId)?.displayName ?? '',
    updatedAt: doc.updatedAt,
    files: publicFiles(cache, doc.bodyJson),
  };
}

function subscribeToCache(notify: () => void): () => void {
  return subscribeKeys([ANY_CHANGE_KEY], notify);
}

interface LocalSnapshot {
  cache: MockCache;
  token: string;
  version: number;
  doc: PublicDoc | null;
}

let localSnapshot: LocalSnapshot | null = null;

function localDoc(token: string): PublicDoc | null {
  const cache = requireCache();
  const version = getVersion(ANY_CHANGE_KEY);
  const known = localSnapshot;
  if (known !== null && known.cache === cache && known.token === token && known.version === version) {
    return known.doc;
  }
  const doc = publicDocFromCache(cache, token);
  localSnapshot = { cache, token, version, doc };
  return doc;
}

export function usePublicDoc(token: string): PublicDoc | null | 'loading' {
  const backend = publicDocBackend;
  const local = useSyncExternalStore(subscribeToCache, () => (backend === null ? localDoc(token) : null));
  const [remote, setRemote] = useState<{ token: string; doc: PublicDoc | null } | null>(null);
  useEffect(() => {
    if (backend === null) {
      return undefined;
    }
    let active = true;
    const settle = (doc: PublicDoc | null): void => {
      if (active) {
        setRemote({ token, doc });
      }
    };
    backend.fetchByToken(token).then(settle, () => settle(null));
    return () => {
      active = false;
    };
  }, [backend, token]);
  if (backend === null) {
    return local;
  }
  return remote !== null && remote.token === token ? remote.doc : 'loading';
}
